import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db } from '../../../../firebase/config';
import { 
    collectionGroup, 
    query, 
    where,
    onSnapshot, 
    doc, 
    updateDoc,
    deleteDoc,
    serverTimestamp
} from "firebase/firestore";

import { useAuth } from '../../../hooks/useAuth';
import { useTableState } from '../hooks/useTableState';
import { useDataHydration } from '../hooks/useDataHydration';

/**
 * useEndososTable - Cerebro modular de negocio para el Tablero de Endosos.
 * Controla la suscripción en tiempo real, hidratación de caché de red y control de modales.
 */
export const useEndososTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS ---
    const [endosos, setEndosos] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [activeItem, setActiveItem] = useState(null);
    const [activePolizaData, setActivePolizaData] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [showFichaModal, setShowFichaModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [alertConfig, setAlertConfig] = useState({ show: false, title: '', message: '', type: 'success' });

    // Ref mutable de bloqueo concurrente
    const activeItemPathRef = useRef(null);

    const { clientsCache, policiesCache } = useDataHydration(endosos);

    // 1. Suscripción a Colección de Endosos de los últimos 3 meses
    useEffect(() => {
        const limiteFecha = new Date();
        limiteFecha.setMonth(limiteFecha.getMonth() - 3);
        const isoStringLimite = limiteFecha.toISOString().split('T')[0];

        const q = query(
            collectionGroup(db, 'endosos'),
            where('fechaCarga', '>=', isoStringLimite)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const raw = snap.docs.map(d => {
                const parts = d.ref.path.split('/');
                return { 
                    id: d.id, 
                    path: d.ref.path, 
                    clienteId: parts[3], 
                    polizaId: parts[5], 
                    ...d.data() 
                };
            }).filter(d => d.type !== 'Refacturacion' && d.tipoGestion !== 'Refacturacion');

            setEndosos(raw);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const itemsTable = useMemo(() => {
        return endosos.map(e => {
            const pKey = `${e.clienteId}|${e.polizaId}`;
            const p = policiesCache[pKey] || {};
            const c = clientsCache[e.clienteId] || {};
            
            let nombreP = (e.clienteNombre || p.clienteNombre || '').trim();
            let apellidoP = (e.clienteApellido || p.clienteApellido || '').trim();
            let displayNombre = `${nombreP} ${apellidoP}`.trim();
            
            if (!displayNombre) {
                displayNombre = c.persona === 'JURIDICA' 
                    ? (c.nombre || '').toUpperCase() 
                    : `${c.nombre || ''} ${c.apellido || ''}`.trim().toUpperCase();
            }
            if (!displayNombre) displayNombre = 'S/D';

            const displayDoc = e.clienteCuit || e.clienteDni || p.clienteCuit || p.clienteDni || c.cuit || c.dni || '-';
            const canal = e.canal || p.canal || '-';
            const displayCanalNombre = e.canalNombre || e.subcanal || p.canalNombre || p.subcanal || '-';

            const rawEstado = String(e.estadoGestion || 'Pendiente').trim();
            const estadoGestion = rawEstado.charAt(0).toUpperCase() + rawEstado.slice(1).toLowerCase();

            return {
                ...e,
                displayNombre,
                displayDoc,
                nPoliza: e.nPoliza || p.nPoliza || 'PENDIENTE',
                compania: e.compania || p.compania || 'S/C',
                riesgo: e.riesgo || p.riesgo || 'S/R',
                subriesgo: e.subriesgo || p.subriesgo || '',
                canal,
                displayCanalNombre,
                quienGestiono: e.quienGestiono || '-',
                estadoGestion,
                subcanal: e.subcanal || p.subcanal || ''
            };
        });
    }, [endosos, clientsCache, policiesCache]);

    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'fechaCarga', direction: 'descending' },
        initialColumns: {
            fechaCarga: true, displayNombre: true, displayDoc: true, nPoliza: true, compania: true,
            riesgo: true, type: true, quienGestiono: true, tramite: true,
            estadoGestion: true, canal: true
        },
        initialFilters: {
            estadoGestion: ['Pendiente', 'Reclamado'] // Sello de filtro por defecto al ingresar
        },
        recordsPerPage: 100
    });

    // Desbloqueo temporal
    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al liberar caso:", e); }
    }, []);

    const handleAction = useCallback(async (item, type) => {
        const rawItem = endosos.find(e => e.id === item.id);
        setActiveItem(rawItem);

        const pKey = `${item.clienteId}|${item.polizaId}`;
        const pData = policiesCache[pKey] || { id: item.polizaId, clienteId: item.clienteId };
        setActivePolizaData(pData);

        if (type === 'poliza') {
            setShowPolicyModal(true);
        } else if (type === 'edit') {
            setShowEditModal(true);
            activeItemPathRef.current = item.path;
            await updateDoc(doc(db, item.path), { 
                bloqueo: { userId: user?.uid, userName: profile?.userName || 'Usuario', timestamp: serverTimestamp() } 
            }).catch(e => {
                console.error("Error bloqueando:", e);
                activeItemPathRef.current = null;
            });
        } else if (type === 'ficha') {
            setShowFichaModal(true);
        } else if (type === 'delete') {
            setShowDeleteConfirm(true);
        }
    }, [endosos, policiesCache, user, profile]);

    const handleSuccess = useCallback((msg) => {
        setShowEditModal(false);
        unlockPoliza(activeItemPathRef.current);
        setActiveItem(null);
        setAlertConfig({ show: true, title: '¡Éxito!', message: msg, type: 'success' });
    }, [unlockPoliza]);

    const confirmDelete = useCallback(async () => {
        if (!activeItem?.path) return;
        try {
            await deleteDoc(doc(db, activeItem.path));
            setShowDeleteConfirm(false);
            setActiveItem(null);
            setAlertConfig({ show: true, title: 'Eliminado', message: 'El endoso ha sido eliminado.', type: 'success' });
        } catch (e) { console.error(e); }
    }, [activeItem]);

    return {
        state: {
            loading,
            user,
            clientsCache,
            activeItem,
            activePolizaData,
            showEditModal,
            showPolicyModal,
            showFichaModal,
            showDeleteConfirm,
            alertConfig,
            itemsTable
        },
        actions: {
            setAlertConfig,
            setShowFichaModal,
            setShowPolicyModal,
            setShowEditModal,
            setShowDeleteConfirm,
            handleAction,
            handleSuccess,
            confirmDelete,
            unlockPoliza,
            setActiveItem,
            setActivePolizaData
        },
        tableState
    };
};